import {
    Inject,
    ISimpleControllerConfiguration,
    ModelEvents,
    SimpleBaseController,
} from '@denishussar/html5-game-engine';
import { Graphics, Point } from 'pixi.js';
import { GameLogic } from '../../Logic/GameLogic';
import { GameModel, InstrumentTypeEnum } from '../../Models/GameModel';
import { IPaletteListItemControllerOptions, PaletteListItemController } from './PaletteListItemController';
import isMobile from 'is-mobile';
import { GameEvent, GameEventSceneButtonClick } from '../../Constants/GameEvent';

export enum PaletteListControllerType {
    vertical,
    horizontal,
}

export interface IPaletteLisControllerOptions {
    type: PaletteListControllerType;
    onClick: (index: number) => void;
}

export class PaletteListController extends SimpleBaseController {
    $model: GameModel;

    private _type: PaletteListControllerType;

    private _margin: number = 20;
    private _mask: Graphics = null;
    private _items: PaletteListItemController[] = [];

    private _scrollPoint: Point = new PIXI.Point();
    private _scrollCurrentPoint: Point = new PIXI.Point();
    private _scrollFriction: number = 5;

    private _selectedItem: PaletteListItemController = null;

    private _savePoint: PIXI.Point;
    private _currentPoint: PIXI.Point;
    private _isDrag = false;
    private _isMovePalette = false;

    public constructor(
        @Inject private _options: IPaletteLisControllerOptions,
        @Inject private _gameLogic: GameLogic,
        @Inject private _modelEvents: ModelEvents,
        @Inject private _model: GameModel,
    ) {
        super('PaletteListController');
    }

    protected getConfiguration(): ISimpleControllerConfiguration {
        return <ISimpleControllerConfiguration>{
            modelWatch: {
                palette: () => {
                    this.updatePaletteList();
                },
            },
            modelEvents: {
                setColor: (color: number, index: number) => {
                    this.selectItem(Math.max(0, index), false);
                },
                setInstrumentType: (value: InstrumentTypeEnum) => {
                    switch (value) {
                        case InstrumentTypeEnum.hand:
                            this.selectItem(0, false);
                            break;
                        case InstrumentTypeEnum.preview:
                            this.selectItem(-1, false);
                            break;
                        case InstrumentTypeEnum.paint:
                            this.selectItem(this._gameLogic.getColorIndex(), false);
                            break;
                    }
                },
                doneColor: (color: number, index: number) => {
                    const controller = this.$childControllers.get<PaletteListItemController>(`item_${color}`);

                    if (controller) {
                        if (this._selectedItem === controller) {
                            this._selectedItem = null;
                        }

                        for (let i = 0; i < this._items.length; i++) {
                            if (this._items[i] === controller) {
                                this._items.splice(this._items.indexOf(controller), 1);
                                this.removeChildController(controller);
                            }
                        }
                    }
                },
            },
        };
    }

    protected onInitialize(): void {
        this._type = this._options.type;// ?? PaletteListControllerType.horizontal;

        if (this.$model.palette.length > 0) {
            this.updatePaletteList();
        }
    }

    protected onResize(width: number, height: number, scale: number) {
        if (!this._currentPoint) {
            return;
        }

        let px = this._scrollPoint.x + this._currentPoint.x - this._savePoint.x;
        let py = this._scrollPoint.y + this._currentPoint.y - this._savePoint.y;

        if (px + this.getItemsWidth() < this._mask.x + this._mask.width + 130) {
            px = this._mask.x + this._mask.width - this.getItemsWidth() + 130;
        }
        if (px > this._mask.x) {
            px = this._mask.x;
        }

        if (py + this.getItemsHeight() < this._mask.y + this._mask.height + 130) {
            py = this._mask.y + this._mask.height - this.getItemsHeight() + 130;
        }
        if (py > this._mask.y) {
            py = this._mask.y;
        }

        this._scrollPoint.x = px;
        this._scrollPoint.y = py;
    }

    protected onUpdate(frameRate: number) {
        this.updateScroll();

        const margin = 20;
        const width = PaletteListItemController.width;
        const height = PaletteListItemController.height;

        const startX = width / 2 + 75;
        const startY = height / 2 + 75;

        for (let i = 1; i < this._items.length; i++) {
            const item = this._items[i];

            if (this._type === PaletteListControllerType.horizontal) {
                item.x = i * (width + margin) + startX + (i > 0 ? this.getScroll() : 0);
                item.y = 0;
            } else {
                item.x = 0;
                item.y = i * (height + margin) + startY + (i > 0 ? this.getScroll() : 0);
            }
        }
    }

    private updateScroll(): void {
        if (this._type === PaletteListControllerType.horizontal) {
            if (Math.abs(this._scrollPoint.x - this._scrollCurrentPoint.x) < 10) {
                this._scrollCurrentPoint.x = this._scrollPoint.x;
            } else {
                this._scrollCurrentPoint.x += (this._scrollPoint.x - this._scrollCurrentPoint.x) / this._scrollFriction;
            }

        } else {
            if (Math.abs(this._scrollPoint.y - this._scrollCurrentPoint.y) < 10) {
                this._scrollCurrentPoint.y = this._scrollPoint.y;
            } else {
                this._scrollCurrentPoint.y += (this._scrollPoint.y - this._scrollCurrentPoint.y) / this._scrollFriction;
            }
        }
    }

    private getScroll(): number {
        return this._type === PaletteListControllerType.horizontal ? this._scrollCurrentPoint.x : this._scrollCurrentPoint.y;
    }

    public getItemsWidth(): number {
        return (PaletteListItemController.width + this._margin) * this._items.length;
    }

    public getItemsHeight(): number {
        return (PaletteListItemController.height + this._margin) * this._items.length + 32;
    }

    public getVisibleItemsWidth(): number {
        return this._mask.width;
    }

    public getVisibleItemsHeight(): number {
        return this._mask.height;
    }

    public setMaskBounds(x: number, y: number, width: number, height: number): void {
        if (!this._mask) {
            this._mask = new Graphics();
            this.display.mask = this._mask;
            this.display.addChild(this._mask);
        }

        this._mask.clear();
        this._mask.beginFill(0x000000, 0.5);
        this._mask.drawRect(x, y, width, height);
        this._mask.endFill();
    }

    public scrollBy(count: number): void {
        if (this._type === PaletteListControllerType.horizontal) {
            const min = 0;
            const max = -(this.getItemsWidth() - this.getVisibleItemsWidth());
            const newValue = this._scrollPoint.x + count * (PaletteListItemController.width + this._margin);

            this._scrollPoint.x = Math.min(min, Math.max(newValue, max));

        } else {
            const min = 0;
            const max = -(this.getItemsHeight() - this.getVisibleItemsHeight());
            const newValue = this._scrollPoint.y + count * (PaletteListItemController.height + this._margin);

            this._scrollPoint.y = Math.min(min, Math.max(newValue, max));
        }
    }

    public selectItem(index: number = -1, dispatchClick: boolean = true): void {
        if (this._selectedItem) {
            this._selectedItem.unselect();
        }

        if (index < 0) {
            return;
        }

        this._selectedItem = this._items.find(item => item.getIndex() === index);

        if (this._selectedItem) {
            this._selectedItem.select();
        }

        if (dispatchClick) {
            if (this._options.onClick) {
                this._options.onClick(index);
            }
        }
    }

    private updatePaletteList(): void {
        const palette = this._gameLogic.getPalette();
        
        for (let i = 1; i < palette.length; i++) {
            const color = palette[i];
            if (this._gameLogic.isFillComplete(color)) {
                console.warn("FILLED")
                continue;
            }

            // TODO это ЖЕСТЬ какой злостный хак(!) правильно удалить все контроллеры -
            // но попытка всё почистить приводит к ошибке "conroller.name undefined"
            if (this.$childControllers.get(`item_${color}`)) {
                console.warn("ERR @")
                continue;
            }

            const symbol = i > 0 ? GameModel.alphabetSymbol[i - 1] : null;


            this._items.push(
                this.addChildController({
                    controller: PaletteListItemController,
                    model: this.$model,
                    options: <IPaletteListItemControllerOptions>{
                        color: color,
                        index: i,
                        progress: this._gameLogic.getFillProgress(color),
                        symbol,
                        onClick: (index: number) => {
                            this._modelEvents.dispatch(this._model, GameEvent.sceneButtonClick, <GameEventSceneButtonClick>{ buttonName: `Color_${index}`});
                            this._gameLogic.setColor(index);
                        },
                    },
                }, `item_${color}`) as PaletteListItemController);
        }

        this.display.interactive = true;

        this.display.on('pointerdown', this.startDragPalette.bind(this));
        this.display.on('pointermove', this.moveDragPalette.bind(this));
        this.display.on('pointerup', this.stopDragPalette.bind(this));
        this.display.on('pointerupoutside', this.stopDragPalette.bind(this));
    }

    private startDragPalette(event: PIXI.interaction.InteractionEvent) {
        if (this._type === PaletteListControllerType.horizontal) {
            const isArrowVisible = this.getItemsWidth() - 130 > this.getVisibleItemsWidth();
            if (!isArrowVisible) {
                return;
            }
        } else {
            const isArrowVisible = this.getItemsHeight() - 130 > this.getVisibleItemsHeight();
            if (!isArrowVisible) {
                return;
            }
        }

        this._isDrag = true;
        this._savePoint = new Point(event.data.global.x, event.data.global.y);

        console.log('drag true');
    }

    private moveDragPalette(event: PIXI.interaction.InteractionEvent) {
        if (!this._isDrag) {
            return;
        }

        this._currentPoint = new Point(event.data.global.x, event.data.global.y);

        if (!this._isMovePalette) {
            if (this._type === PaletteListControllerType.horizontal) {
                if (Math.abs(this._savePoint.x - this._currentPoint.x) > 32) {
                    this._savePoint = this._currentPoint;
                    this._isMovePalette = true;
                }
            } else {
                if (Math.abs(this._savePoint.y - this._currentPoint.y) > 32) {
                    this._savePoint = this._currentPoint;
                    this._isMovePalette = true;
                }
            }
            return;
        }

        let px = 0;
        let py = 0;

        if (this._type === PaletteListControllerType.horizontal) {
            px = this._scrollPoint.x + this._currentPoint.x - this._savePoint.x;
            if (px > this._mask.x) {
                px = this._mask.x;
            }
            if (px + this.getItemsWidth() < this._mask.x + this._mask.width + 130) {
                px = this._mask.x + this._mask.width - this.getItemsWidth() + 130;
            }
        } else {
            py = this._scrollPoint.y + this._currentPoint.y - this._savePoint.y;
            if (py > this._mask.y) {
                py = this._mask.y;
            }
            if (py + this.getItemsHeight() < this._mask.y + this._mask.height + 130) {
                py = this._mask.y + this._mask.height - this.getItemsHeight() + 130;
            }
        }

        this._scrollPoint.x = px;
        this._scrollPoint.y = py;
        this._savePoint = this._currentPoint;
    }

    private stopDragPalette() {
        this._isDrag = false;
    }

    get items() {
        return this._items;
    }
}
